import FilmReelLogo from 'assets/icon/filmReel.png';
import { AuthorizeParamsResponse, getAuthorizeParams } from 'services/oauthApi';

const OAUTH_FLOW_ACTIVE_KEY = 'oauth_flow_active';
const OAUTH_CLIENT_ID_KEY = 'oauth_client_id';
const OAUTH_STATE_KEY = 'oauth_state';
const OAUTH_LOGIN_METHOD_KEY = 'oauth_login_method';

export type OAuthLoginMethod = 'email' | 'phone';

const OAUTH_CLIENT_LOGOS: Record<string, string> = {
  'app-b-client': FilmReelLogo,
  'film-reel': FilmReelLogo
};

export interface OAuthQueryParseResult {
  isOAuthMode: boolean;
  clientId: string | null;
  state: string | null;
  clientName: string | null;
  authorizeParams: AuthorizeParamsResponse | null;
}

export const parseOAuthQuery = (searchParams: URLSearchParams): OAuthQueryParseResult => {
  const clientId = searchParams.get('client_id');
  const state = searchParams.get('state');
  // OAuth flow whenever client_id is present (state needed for APIs later)
  const isOAuthMode = Boolean(clientId);
  if (isOAuthMode && clientId) {
    persistOAuthFlow(clientId, state);
  }
  return {
    isOAuthMode,
    clientId,
    state,
    clientName: null,
    authorizeParams: null
  };
};

export const persistOAuthFlow = (clientId: string, state: string | null): void => {
  try {
    sessionStorage.setItem(OAUTH_FLOW_ACTIVE_KEY, 'true');
    sessionStorage.setItem(OAUTH_CLIENT_ID_KEY, clientId);
    if (state) {
      sessionStorage.setItem(OAUTH_STATE_KEY, state);
    }
  } catch {
    // sessionStorage may be unavailable
  }
};

export const clearOAuthFlow = (): void => {
  try {
    sessionStorage.removeItem(OAUTH_FLOW_ACTIVE_KEY);
    sessionStorage.removeItem(OAUTH_CLIENT_ID_KEY);
    sessionStorage.removeItem(OAUTH_STATE_KEY);
    sessionStorage.removeItem(OAUTH_LOGIN_METHOD_KEY);
  } catch {
    // ignore
  }
};

export const persistOAuthLoginMethod = (method: OAuthLoginMethod): void => {
  try {
    sessionStorage.setItem(OAUTH_LOGIN_METHOD_KEY, method);
  } catch {
    // sessionStorage may be unavailable
  }
};

export const getOAuthLoginMethod = (): OAuthLoginMethod | null => {
  try {
    const value = sessionStorage.getItem(OAUTH_LOGIN_METHOD_KEY);
    if (value === 'email' || value === 'phone') {
      return value;
    }
    return null;
  } catch {
    return null;
  }
};

export const getStoredOAuthClientId = (): string | null => {
  try {
    return sessionStorage.getItem(OAUTH_CLIENT_ID_KEY);
  } catch {
    return null;
  }
};

export const getStoredOAuthState = (): string | null => {
  try {
    return sessionStorage.getItem(OAUTH_STATE_KEY);
  } catch {
    return null;
  }
};

/** True when URL has client_id or sessionStorage OAuth flag is set (e.g. /consent?state=…). */
export const isOAuthFlowActive = (): boolean => {
  try {
    if (typeof window !== 'undefined') {
      const params = new URLSearchParams(window.location.search);
      if (params.get('client_id')) {
        return true;
      }
    }
    return sessionStorage.getItem(OAUTH_FLOW_ACTIVE_KEY) === 'true';
  } catch {
    return false;
  }
};

export const getOAuthClientLogo = (clientId?: string | null): string | null =>
  clientId ? OAUTH_CLIENT_LOGOS[clientId] ?? null : null;


export const loadAuthorizeParams = async (state: string): Promise<AuthorizeParamsResponse> => {
  return getAuthorizeParams(state);
};
